import React, { useEffect, useState } from 'react';
import { Drawer, Menu, Button, Divider, Modal } from 'antd';
import type { MenuProps } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './MobileMenu.scss';
import { MenuItem } from 'types/common';
import {
  createMainMenuItems,
  createProfileMenuItems,
  createSignOutMenuItems,
} from 'routes/menu';
import chatIcon from 'assets/images/chat.png';
import languageIcon from 'assets/images/language.png';
import { derivePathKeys } from 'utils/commonUtils';
import { useSession } from 'context/SessionContext';
import { buildLoginPath } from 'utils/pathBuilder';
import NeedHelp from 'components/needHelp/NeedHelp';

interface MobileMenuProps {
  visible: boolean;
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ visible, onClose }) => {
  const { t, i18n } = useTranslation('layout');
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { session, clearSession } = useSession();
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);

  // Auto-close drawer when screen width exceeds desktop breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 576 && visible) {
        onClose();
      }
    };

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [visible, onClose]);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const cleanPath = e.keyPath.reverse().join('/');
    const lang = i18n.language || 'en';
    const fullPath = `/${lang}/${cleanPath}`;

    //close drawer first
    onClose();

    if(cleanPath.includes('signOut')){
      //sign out
      clearSession();
      navigate(buildLoginPath(), { replace: true });
    }else{
      //navigate
      navigate(fullPath);
    }
  };

  useEffect(() => {
    const { parent, child } = derivePathKeys(
      location.pathname,
      String(process.env.PUBLIC_URL)
    );
    setSelectedKeys(child ? [child] : parent ? [parent] : []);
    setOpenKeys(parent ? [parent] : []);
  }, [location.pathname]);

  const handleLanguageChange = () => {
    const currentLang = i18n.language;
    const newLang = currentLang === 'en' ? 'zh' : 'en';
    i18n.changeLanguage(newLang);
    navigate(location.pathname.replace(`/${currentLang}`, `/${newLang}`), { replace: true });
  };


  const handleHelp = () => {
    setIsHelpModalVisible(true);
  };

  // Consolidated menu items with dividers for visual separation
  const mainMenuItems = createMainMenuItems(t);
  const profileMenuItems = createProfileMenuItems(t);
  const signOutMenuItems = createSignOutMenuItems(t);
  const allMenuItems: MenuItem[] = [
    ...mainMenuItems,
    { type: 'divider' },
    ...profileMenuItems,
    { type: 'divider' },
    ...signOutMenuItems,
  ];

  return (
    <>
      <Drawer
        title={null}
        placement="left"
        onClose={onClose}
        open={visible}
        width={'100%'}
        className="mobile-menu-drawer"
        closable={false}
        styles={{ body: { padding: 0 } }}
      >
        <div className="mobile-menu-container">
          {/* Header with close button */}
          <div className="mobile-menu-header">
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              className="close-button"
            />
          </div>

          {session && (
            <div className="main-menu-section">
              <Menu
                mode="inline"
                onClick={handleMenuClick}
                className="mobile-menu"
                items={allMenuItems}
                selectedKeys={selectedKeys}
                openKeys={openKeys}
                onOpenChange={(keys) => setOpenKeys(keys as string[])}
              />
            </div>
          )}

          {/* Bottom Actions */}
          <div className="bottom-actions">
            <Button
              type="link"
              icon={<img src={chatIcon} alt="Chat" className="chat-icon" />}
              onClick={handleHelp}
              className="help-button"
            >
              {t('menu.needHelp')}
            </Button>
            <Divider type="vertical" />
            <Button
              type="link"
              icon={
                <img
                  src={languageIcon}
                  alt="Language"
                  className="language-icon"
                />
              }
              onClick={handleLanguageChange}
              className="language-button"
            >
              {i18n.language === 'zh'
                ? t('language.english')
                : t('language.chinese')}
            </Button>
          </div>
        </div>
      </Drawer>

      <Modal
        open={isHelpModalVisible}
        onCancel={() => setIsHelpModalVisible(false)}
        footer={null}
        centered
        closable={true}
        width="90%"
        // destroyOnClose={true}
      >
        <div style={{ padding: '20px 0' }}>
          <NeedHelp />
        </div>
      </Modal>
    </>
  );
};

export default MobileMenu;